import type { Page } from "@emkit/builder-core";
import { renderPage } from "@emkit/builder-runtime";
import { createBuiltinRegistry } from "@emkit/builder-widgets";
import { useRef } from "react";
import { useEditor, useEditorHistory } from "../store.js";
import { BreakpointSwitcher } from "./BreakpointSwitcher.js";

// Shared registry for HTML export. Cheap — just lookup tables, no
// state, safe to reuse across downloads.
const exportRegistry = createBuiltinRegistry();

function download(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function slugify(slug: string): string {
  const cleaned = slug.replace(/\//g, "").trim();
  return cleaned || "page";
}

/**
 * Structural check for imported JSON. We don't want a malformed file to
 * corrupt the editor state, but we also don't want to be so strict that
 * future schema additions break imports. Check just the shape we depend
 * on; widgets are validated individually at render time anyway.
 */
function isPageLike(v: unknown): v is Page {
  if (!v || typeof v !== "object") return false;
  const p = v as Record<string, unknown>;
  if (typeof p.id !== "string") return false;
  if (typeof p.title !== "string") return false;
  if (typeof p.slug !== "string") return false;
  if (!Array.isArray(p.sections)) return false;
  for (const s of p.sections as unknown[]) {
    if (!s || typeof s !== "object") return false;
    const section = s as Record<string, unknown>;
    if (typeof section.id !== "string") return false;
    if (!Array.isArray(section.columns)) return false;
  }
  return true;
}

export function Toolbar() {
  const mode = useEditor((s) => s.mode);
  const setMode = useEditor((s) => s.setMode);
  const page = useEditor((s) => s.page);
  const setPage = useEditor((s) => s.setPage);
  const openGallery = useEditor((s) => s.openGallery);
  const openPageSettings = useEditor((s) => s.openPageSettings);
  const openAISettings = useEditor((s) => s.openAISettings);

  const history = useEditorHistory();
  const canUndo = history.pastStates.length > 0;
  const canRedo = history.futureStates.length > 0;

  const onUndo = () => useEditor.temporal.getState().undo();
  const onRedo = () => useEditor.temporal.getState().redo();

  const importInputRef = useRef<HTMLInputElement>(null);
  const onImportClick = () => importInputRef.current?.click();
  const onImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file twice re-triggers onChange.
    e.target.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!isPageLike(parsed)) {
        window.alert("That file doesn't look like an EmBuilder page export.");
        return;
      }
      setPage(parsed);
    } catch (err) {
      window.alert(
        `Couldn't read that file: ${err instanceof Error ? err.message : "parse error"}`,
      );
    }
  };

  const onExportJson = () => {
    const blob = new Blob([JSON.stringify(page, null, 2)], {
      type: "application/json",
    });
    download(`${slugify(page.slug)}.json`, blob);
  };

  const onExportHtml = () => {
    const html = renderPage(page, {
      registry: exportRegistry,
      fullDocument: true,
      breakpoint: "desktop",
    });
    const blob = new Blob([html], { type: "text/html" });
    download(`${slugify(page.slug)}.html`, blob);
  };

  return (
    <div className="emke-toolbar">
      <div className="emke-logo">
        Em<span>Builder</span>
      </div>

      <div style={{ display: "flex", gap: 6, marginLeft: 16 }}>
        <button
          type="button"
          className="emke-btn"
          onClick={onUndo}
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
          style={{ opacity: canUndo ? 1 : 0.4 }}
        >
          ↶ Undo
        </button>
        <button
          type="button"
          className="emke-btn"
          onClick={onRedo}
          disabled={!canRedo}
          title="Redo (Ctrl+Shift+Z)"
          style={{ opacity: canRedo ? 1 : 0.4 }}
        >
          ↷ Redo
        </button>
      </div>

      <button
        type="button"
        className="emke-btn"
        onClick={openGallery}
        title="Pick a template"
        style={{ marginLeft: 8 }}
      >
        ✨ Templates
      </button>

      <button
        type="button"
        className="emke-btn"
        onClick={openPageSettings}
        title="Edit page title, slug, description, OG image"
        style={{ marginLeft: 4 }}
      >
        ⚙ Page settings
      </button>

      <button
        type="button"
        className="emke-btn"
        onClick={openAISettings}
        title="Configure AI provider (OpenAI / Gemini / Grok / Claude)"
        style={{ marginLeft: 4 }}
      >
        ⚡ AI
      </button>

      <div
        style={{
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <BreakpointSwitcher />
      </div>

      <div className="emke-tabs">
        <button
          type="button"
          className={`emke-tab ${mode === "edit" ? "is-active" : ""}`}
          onClick={() => setMode("edit")}
        >
          Edit
        </button>
        <button
          type="button"
          className={`emke-tab ${mode === "preview" ? "is-active" : ""}`}
          onClick={() => setMode("preview")}
        >
          Preview
        </button>
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        <button
          type="button"
          className="emke-btn"
          onClick={onImportClick}
          title="Load a page JSON exported earlier"
        >
          Import JSON
        </button>
        <input
          ref={importInputRef}
          type="file"
          accept="application/json,.json"
          onChange={onImportFile}
          style={{ display: "none" }}
          aria-hidden="true"
          tabIndex={-1}
        />
        <button
          type="button"
          className="emke-btn"
          onClick={onExportHtml}
          title="Download standalone HTML file"
        >
          Export HTML
        </button>
        <button
          type="button"
          className="emke-btn"
          onClick={onExportJson}
          title="Download page JSON (re-importable)"
        >
          Export JSON
        </button>
      </div>
    </div>
  );
}
